/**
 * ImageCropper Component
 * - 1:1 aspect ratio for profile photos
 * - 4:3 aspect ratio for car/document photos
 * - Manual cropping with preview
 */
import React, { useState, useRef, useCallback } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Crop, RotateCw, Check, X } from "lucide-react";

const ImageCropper = ({ 
  isOpen, 
  onClose, 
  onCropComplete, 
  aspectRatio = 1, // 1 = 1:1 for profile, 4/3 for car
  imageType = "profile", // "profile" or "vehicle"
  initialFile = null // Optional: pre-load a file
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 80,
    aspect: aspectRatio
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [rotation, setRotation] = useState(0);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-load initialFile when provided
  React.useEffect(() => {
    if (isOpen && initialFile) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
        setRotation(0);
      });
      reader.readAsDataURL(initialFile);
    }
    if (!isOpen) {
      setImageSrc(null);
      setCompletedCrop(null);
      setRotation(0);
    }
  }, [isOpen, initialFile]);

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result);
        setRotation(0);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    
    // Center the crop
    const { width, height } = e.currentTarget;
    const cropWidth = Math.min(80, (height / width) * 80 * aspectRatio);
    
    setCrop({
      unit: "%",
      width: cropWidth,
      aspect: aspectRatio,
      x: (100 - cropWidth) / 2,
      y: 10
    });
  }, [aspectRatio]);

  const rotateImage = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return null;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Calculate rotated dimensions
    const radians = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(radians));
    const cos = Math.abs(Math.cos(radians));

    const pixelCrop = {
      x: completedCrop.x * scaleX,
      y: completedCrop.y * scaleY,
      width: completedCrop.width * scaleX,
      height: completedCrop.height * scaleY
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Apply rotation
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(radians);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(null);
            return;
          }
          
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              blob,
              base64: reader.result,
              width: canvas.width,
              height: canvas.height
            });
          };
          reader.readAsDataURL(blob);
        },
        "image/jpeg",
        0.9
      );
    });
  }, [completedCrop, rotation]);

  const handleCropComplete = async () => {
    const croppedImage = await getCroppedImg();
    if (croppedImage) {
      onCropComplete(croppedImage);
      handleClose();
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ unit: "%", width: 80, aspect: aspectRatio });
    setCompletedCrop(null);
    setRotation(0);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-slate-800 border-slate-700">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Crop className="h-5 w-5" />
            Crop {imageType === "profile" ? "Profile Photo" : "Vehicle Photo"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!imageSrc ? (
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-600 rounded-lg">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={onSelectFile}
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Select Photo
              </Button>
              <p className="text-sm text-gray-400 mt-2">
                {imageType === "profile" 
                  ? "Square crop (1:1) - Best for profile pictures" 
                  : "Landscape crop (4:3) - Best for vehicle photos"}
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-center bg-slate-900 rounded-lg p-4 max-h-96 overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                  className="max-h-80"
                >
                  <img
                    src={imageSrc}
                    onLoad={onImageLoad}
                    alt="Crop preview"
                    style={{ 
                      transform: `rotate(${rotation}deg)`,
                      maxHeight: "320px"
                    }}
                  />
                </ReactCrop>
              </div>

              <div className="flex justify-center gap-4">
                <Button
                  onClick={rotateImage}
                  variant="outline"
                  className="border-slate-600"
                >
                  <RotateCw className="h-4 w-4 mr-2" />
                  Rotate
                </Button>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="border-slate-600"
                >
                  Change Photo
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={onSelectFile}
                  className="hidden"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            onClick={handleClose}
            variant="outline"
            className="border-slate-600"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          {imageSrc && (
            <Button
              onClick={handleCropComplete}
              className="bg-emerald-500 hover:bg-emerald-600"
              disabled={!completedCrop}
            >
              <Check className="h-4 w-4 mr-2" />
              Save Photo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
